// src/components/TimeMachine.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import {
  VerticalTimeline,
  VerticalTimelineElement
} from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import '../css/TimeMachine.css';
import javaVersions from '../data';

const panelVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1 }
};

export default function TimeMachine({ selected, onClose }) {
  const entries = javaVersions
    .reduce((acc, { version, date, features }) => {
      features.forEach(f => {
        const rawName = f.metadata.feature_name;
        const baseName = rawName.replace(/\s*\((Preview|Incubator)\)$/i, '');
        if (baseName === selected) {
          let phase = 'Final';
          if (/\(Preview\)$/i.test(rawName)) phase = 'Preview';
          else if (/\(Incubator\)$/i.test(rawName)) phase = 'Incubator';
          acc.push({
            version,
            date,
            phase,
            description: f.metadata.description,
            blog_post: f.metadata.blog_post
          });
        }
      });
      return acc;
    }, [])
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const [index, setIndex] = useState(0);
  const [width, setWidth] = useState(window.innerWidth);

  // Lock background scroll
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, []);

  // Handle resize
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Clamp index
  useEffect(() => {
    if (index >= entries.length) setIndex(entries.length - 1);
  }, [entries, index]);

  const isMobile = width < 768;
  const handlers = useSwipeable({
    onSwipedLeft:  () => setIndex(i => Math.min(i + 1, entries.length - 1)),
    onSwipedRight: () => setIndex(i => Math.max(i - 1, 0)),
    trackMouse: true
  });

  const renderCard = ({ version, date, phase, description, blog_post }) => {
    const color =
      phase === 'Incubator' ? 'var(--color-accent)' :
      phase === 'Preview'   ? 'var(--color-primary)' :
                               'var(--color-text)';

    return (
      <div className="tm-card">
        <h3 className="tm-card-title">Java {version}</h3>
        <h4 className="tm-card-phase" style={{ color }}>{phase}</h4>
        <p className="tm-card-date">{date}</p>
        {description && <p className="tm-card-description">{description}</p>}
        {blog_post && (
          <p className="tm-card-link">
            <a href={blog_post} target="_blank" rel="noopener noreferrer">Read more…</a>
          </p>
        )}
      </div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        className="tm-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="tm-modal"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={panelVariants}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          onClick={e => e.stopPropagation()}
        >
          <button className="tm-close" onClick={onClose} aria-label="Close">×</button>
          <h2 className="tm-header">Time Machine: {selected}</h2>

          {/* Legend */}
          <div className="tm-legend">
            <span className="legend-item">
              <span className="legend-color incubator"></span> Incubator
            </span>
            <span className="legend-item">
              <span className="legend-color preview"></span> Preview
            </span>
            <span className="legend-item">
              <span className="legend-color final"></span> Final
            </span>
          </div>

          {isMobile ? (
            <div {...handlers} className="tm-swipe-container">
              {entries.length > 0 && renderCard(entries[index])}
              <div className="tm-swipe-controls">
                <button onClick={() => setIndex(i => Math.max(i - 1, 0))} disabled={index === 0}>← Prev</button>
                <button onClick={() => setIndex(i => Math.min(i + 1, entries.length - 1))} disabled={index === entries.length - 1}>Next →</button>
              </div>
            </div>
          ) : (
            <VerticalTimeline>
              {entries.map((entry, idx) => (
                <VerticalTimelineElement
                  key={`${entry.version}-${entry.phase}-${idx}`}
                  date={entry.date}
                  iconStyle={{
                    background: entry.phase === 'Incubator' ? 'var(--color-accent)' : entry.phase === 'Preview' ? 'var(--color-primary)' : 'var(--color-text)',
                    color: '#fff'
                  }}
                  contentStyle={{
                    borderTop: `3px solid ${entry.phase === 'Incubator' ? 'var(--color-accent)' : entry.phase === 'Preview' ? 'var(--color-primary)' : 'var(--color-text)'}`
                  }}
                >
                  <h3 className="vertical-timeline-element-title">Java {entry.version}</h3>
                  <h4 className="vertical-timeline-element-subtitle" style={{ color: entry.phase === 'Incubator' ? 'var(--color-accent)' : entry.phase === 'Preview' ? 'var(--color-primary)' : 'var(--color-text)' }}>
                    {entry.phase}
                  </h4>
                  {entry.description && <p className="tm-card-description">{entry.description}</p>}
                  {entry.blog_post && <p className="tm-card-link"><a href={entry.blog_post} target="_blank" rel="noopener noreferrer">Read more…</a></p>}
                </VerticalTimelineElement>
              ))}
            </VerticalTimeline>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
